import { validateNumeric2DArray, validateArray, validateShapesMatch, validateFitted, validatePositiveInteger } from '../utils/validation.js';
import { mode } from '../utils/stats.js';

class TreeNode {
  constructor(options = {}) {
    this.featureIndex = options.featureIndex || null;
    this.threshold = options.threshold || null;
    this.left = options.left || null;
    this.right = options.right || null;
    this.value = options.value || null;
    this.isLeaf = options.isLeaf || false;
  }
}

export class DecisionTreeClassifier {
  constructor(options = {}) {
    this.maxDepth = options.maxDepth || 10;
    this.minSamplesSplit = options.minSamplesSplit || 2;
    this.minSamplesLeaf = options.minSamplesLeaf || 1;
    this.criterion = options.criterion || 'gini';

    this.root = null;
    this.classes = null;
    this.isFitted = false;
  }

  _giniImpurity(y) {
    const counts = {};
    for (const label of y) {
      counts[label] = (counts[label] || 0) + 1;
    }

    let impurity = 1;
    for (const count of Object.values(counts)) {
      const prob = count / y.length;
      impurity -= prob * prob;
    }

    return impurity;
  }

  _entropy(y) {
    const counts = {};
    for (const label of y) {
      counts[label] = (counts[label] || 0) + 1;
    }

    let entropy = 0;
    for (const count of Object.values(counts)) {
      const prob = count / y.length;
      if (prob > 0) {
        entropy -= prob * Math.log2(prob);
      }
    }

    return entropy;
  }

  _calculateImpurity(y) {
    return this.criterion === 'gini' ? this._giniImpurity(y) : this._entropy(y);
  }

  _split(X, y, featureIndex, threshold) {
    const leftIndices = [];
    const rightIndices = [];

    for (let i = 0; i < X.length; i++) {
      if (X[i][featureIndex] <= threshold) {
        leftIndices.push(i);
      } else {
        rightIndices.push(i);
      }
    }

    return {
      leftX: leftIndices.map(i => X[i]),
      leftY: leftIndices.map(i => y[i]),
      rightX: rightIndices.map(i => X[i]),
      rightY: rightIndices.map(i => y[i])
    };
  }

  _findBestSplit(X, y) {
    let bestGain = -Infinity;
    let bestFeature = null;
    let bestThreshold = null;

    const nFeatures = X[0].length;
    const currentImpurity = this._calculateImpurity(y);

    for (let featureIndex = 0; featureIndex < nFeatures; featureIndex++) {
      const featureValues = Array.from(new Set(X.map(row => row[featureIndex]))).sort((a, b) => a - b);

      for (let i = 0; i < featureValues.length - 1; i++) {
        const threshold = (featureValues[i] + featureValues[i + 1]) / 2;

        const { leftY, rightY } = this._split(X, y, featureIndex, threshold);

        if (leftY.length < this.minSamplesLeaf || rightY.length < this.minSamplesLeaf) {
          continue;
        }

        const leftImpurity = this._calculateImpurity(leftY);
        const rightImpurity = this._calculateImpurity(rightY);

        const weightedImpurity =
          (leftY.length / y.length) * leftImpurity +
          (rightY.length / y.length) * rightImpurity;

        const gain = currentImpurity - weightedImpurity;

        if (gain > bestGain) {
          bestGain = gain;
          bestFeature = featureIndex;
          bestThreshold = threshold;
        }
      }
    }

    return { featureIndex: bestFeature, threshold: bestThreshold, gain: bestGain };
  }

  _buildTree(X, y, depth = 0) {
    if (depth >= this.maxDepth ||
        y.length < this.minSamplesSplit ||
        new Set(y).size === 1) {
      return new TreeNode({
        value: mode(y),
        isLeaf: true
      });
    }

    const { featureIndex, threshold, gain } = this._findBestSplit(X, y);

    if (gain <= 0 || featureIndex === null) {
      return new TreeNode({
        value: mode(y),
        isLeaf: true
      });
    }

    const { leftX, leftY, rightX, rightY } = this._split(X, y, featureIndex, threshold);

    const leftNode = this._buildTree(leftX, leftY, depth + 1);
    const rightNode = this._buildTree(rightX, rightY, depth + 1);

    return new TreeNode({
      featureIndex,
      threshold,
      left: leftNode,
      right: rightNode,
      isLeaf: false
    });
  }

  fit(X, y) {
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');
    validateArray(y, 'y');
    validateShapesMatch(X, y);

    this.classes = Array.from(new Set(y)).sort();
    this.root = this._buildTree(X, y);

    this.isFitted = true;
    return this;
  }

  _predictOne(x, node = this.root) {
    if (node.isLeaf) {
      return node.value;
    }

    if (x[node.featureIndex] <= node.threshold) {
      return this._predictOne(x, node.left);
    } else {
      return this._predictOne(x, node.right);
    }
  }

  predict(X) {
    validateFitted(this.isFitted, 'DecisionTreeClassifier');

    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');

    return X.map(x => this._predictOne(x));
  }

  score(X, y) {
    validateFitted(this.isFitted, 'DecisionTreeClassifier');

    const predictions = this.predict(X);
    const correct = predictions.filter((pred, i) => pred === y[i]).length;
    return correct / y.length;
  }
}
